/* eslint-disable tree-shaking/no-side-effects-in-initialization */
import { gql } from "@apollo/client";
import {
  GLOBAL_ORG_SHARING_ENVIRONMENTS,
  ORG_IDENTIFIER_REGEX,
  OrgId,
  RESERVED_ORG_IDS,
  getProductionEnvironment,
} from "@hex/common";

import { safeLocalStorage } from "./util/browserStorage.js";
import { getMcpOrgId } from "./util/mcpOverrides";

gql`
  fragment BasicOrgFragment on Org {
    id
    displayName
  }
`;

gql`
  query OrgsClient {
    orgsClient @client {
      orgs {
        id
        ...BasicOrgFragment
      }
      loaded
    }
  }
`;

gql`
  fragment BasicOrgFragmentForPicker on User {
    id
    email
    org {
      id
      ...BasicOrgFragment
      ssoEnforced
    }
  }
`;

gql`
  fragment WorkspacePickerFragment on User {
    id
    orgRole
    email
    org {
      id
      ...BasicOrgFragment
      userCount
    }
  }
`;

gql`
  query OrgsPickerClient {
    orgPickerClient @client {
      authenticatedUsers {
        id
        ...WorkspacePickerFragment
      }
      unauthenticatedUsers {
        id
        ...BasicOrgFragmentForPicker
      }
      loaded
    }
  }
`;

/**
 * Accept an org URL segment (from the injected `data-hex-org`, the URL path, or
 * storage) only if it's a well-formed org identifier: lowercase alphanumerics,
 * `_`, and `-`. Every real org `id`/`userFacingId` matches this, so it rejects
 * only malformed or tampered values and, by construction, strips any HTML/URL
 * meta-characters before the segment can reach an href.
 */
const asOrgSegment = (value: string | null | undefined): string | undefined =>
  value != null && ORG_IDENTIFIER_REGEX.test(value) ? value : undefined;

export const getUrlOrg = (): OrgId | undefined => {
  const mcpOverrideOrgId = getMcpOrgId();
  const potentialOrgName =
    mcpOverrideOrgId ?? window.location.pathname.split("/")[1];

  // Filter non-org base paths
  const isBaseRoute =
    potentialOrgName != null && RESERVED_ORG_IDS.includes(potentialOrgName);

  return potentialOrgName != null && potentialOrgName !== "" && !isBaseRoute
    ? (asOrgSegment(potentialOrgName) as OrgId | undefined)
    : undefined;
};

export const LAST_ORG_STORAGE_KEY = "lastOrganization";
export const GLOBAL_ORG_ID = (process.env.GLOBAL_ORG_ID ?? "global") as OrgId;

interface InjectedOrg {
  id: OrgId;
  userFacingId: string | null;
}

/**
 * The org the server resolved for the current URL, injected into the HTML shell
 * as `data-hex-org` on `<head>`. The URL's first path segment may be either the
 * canonical `id` or the user-facing handle (`userFacingId`); this mapping lets
 * us send the canonical `id` on the wire while keeping the handle in the URL.
 * Absent on non-org routes (login, signup, org picker) and pre-injection builds.
 */
const getInjectedOrg = (): InjectedOrg | undefined => {
  if (typeof document === "undefined") {
    return undefined;
  }
  const raw = document.head?.getAttribute("data-hex-org");
  if (raw == null || raw === "") {
    return undefined;
  }
  try {
    const parsed = JSON.parse(raw) as InjectedOrg;
    // Validate the injected values against the org-identifier charset before
    // trusting them: rejects a tampered attribute and strips meta-characters so
    // the id/handle is safe to route and interpolate into hrefs.
    const id = asOrgSegment(parsed?.id);
    if (id == null) {
      return undefined;
    }
    return {
      id: id as OrgId,
      userFacingId: asOrgSegment(parsed.userFacingId) ?? null,
    };
  } catch {
    return undefined;
  }
};

// Avoid using this function directly, and instead use the `ORG_ID` singleton. This is
// to avoid the bug addressed here: https://github.com/hex-inc/hex/pull/11165
const getCurrentOrg = (): OrgId => {
  // The server-resolved canonical id is the only value safe to persist: the URL
  // segment may be a user-facing handle, and the wire requires the canonical id.
  const injectedId = getInjectedOrg()?.id;
  if (injectedId != null) {
    safeLocalStorage.setItem(LAST_ORG_STORAGE_KEY, injectedId);
    return injectedId as OrgId;
  }

  // Fall back to the URL segment (which may be a handle on the degraded path
  // where injection is absent) or the last persisted canonical id — but do NOT
  // persist a URL-derived value, to avoid poisoning `lastOrganization` with a
  // handle that most of the API surface can't resolve.
  const fallback =
    getUrlOrg() ?? asOrgSegment(safeLocalStorage.getItem(LAST_ORG_STORAGE_KEY));

  return (fallback ?? GLOBAL_ORG_ID) as OrgId;
};

export const ORG_ID = getCurrentOrg();

/**
 * The path segment used to *display* and route the org in URLs: the user-facing
 * handle when one is set, otherwise the canonical id. Use this for building
 * hrefs and the router basename; use {@link ORG_ID} for API calls.
 */
const getOrgUrlSegment = (): OrgId => {
  const injected = getInjectedOrg();
  return (injected?.userFacingId ??
    injected?.id ??
    getUrlOrg() ??
    ORG_ID) as OrgId;
};

export const ORG_URL_SEGMENT = getOrgUrlSegment();

const environment = getProductionEnvironment(window.location.hostname);
// Temporary hack until we remove global org. For single-tenant/self-hosted
// enable org sharing even in the global org
export const ORG_SHARING_ENABLED =
  ORG_ID !== GLOBAL_ORG_ID ||
  GLOBAL_ORG_SHARING_ENVIRONMENTS.includes(environment);
